import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wheat, Handshake, Warehouse, ShieldCheck, TrendingUp, Lightbulb, ChevronRight, MessageCircle, Banknote, Truck, Snowflake } from 'lucide-react';
import { companyInfo, aboutContent, services, stats, whyChooseUs, images, blogs } from '../data/mock';

const HomePage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const iconMap = {
    Wheat: Wheat,
    Handshake: Handshake,
    Warehouse: Warehouse,
    ShieldCheck: ShieldCheck,
    TrendingUp: TrendingUp,
    Lightbulb: Lightbulb,
    Banknote: Banknote,
    Truck: Truck,
    Snowflake: Snowflake,
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAFFEE] via-[#FAFFEE] to-[#EDEDFE]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-40 w-96 h-96 bg-[#CACAFC]/30 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-[#004534]/10 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-[#D3FF62] rounded-full animate-pulse" />
                <span className="text-[#004534] font-medium text-sm">Since {companyInfo.founded}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#004534] leading-tight">
                Building a Financially <span className="text-[#0C6951]">Stronger Future</span> for Farmers
              </h1>
              
              <p className="text-lg text-[#0C6951] leading-relaxed max-w-xl">
                {companyInfo.subTagline}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#004534] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0C6951] transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#004534]/20"
                >
                  Partner With Us
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-[#004534] text-[#004534] px-8 py-4 rounded-full font-semibold hover:bg-[#004534] hover:text-white transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-[#004534]">{stat.value}</div>
                    <div className="text-sm text-[#807979]">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={images.hero}
                  alt="Indian farmer in field"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#004534]/40 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs animate-on-scroll opacity-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D3FF62] rounded-full flex items-center justify-center">
                    <Wheat className="w-6 h-6 text-[#004534]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#004534]">Agricultural Produce</div>
                    <div className="text-sm text-[#807979]">Pan-India Supply Network</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative animate-on-scroll opacity-0">
              <div className="grid grid-cols-2 gap-4">
                <img src={images.farming} alt="Indian farmer in rice paddy" className="rounded-2xl w-full h-64 object-cover" />
                <img src={images.produce} alt="Wheat harvest bundles" className="rounded-2xl w-full h-64 object-cover mt-8" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#D3FF62] rounded-2xl p-6">
                <div className="text-4xl font-bold text-[#004534]">60+</div>
                <div className="text-[#004534] font-medium">Years of Legacy</div>
              </div>
            </div>
            
            <div className="space-y-6 animate-on-scroll opacity-0">
              <span className="text-[#0C6951] font-semibold uppercase tracking-wider">Who We Are</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] leading-tight">
                Your Trusted Partner in Agricultural Produce Management
              </h2>
              <p className="text-[#0C6951] leading-relaxed">
                {aboutContent.history.description}
              </p>
              <p className="text-[#807979] leading-relaxed">
                {companyInfo.description}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#004534] font-semibold hover:gap-4 transition-all duration-200"
              >
                Read Our Story <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#FAFFEE]">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16 animate-on-scroll opacity-0">
            <span className="text-[#0C6951] font-semibold uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mt-4 mb-6">
              Comprehensive Agricultural Solutions
            </h2>
            <p className="text-[#807979]">
              From farm to business, we provide end-to-end solutions that empower farmers and serve industries
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-[#FAFAFF] rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-on-scroll opacity-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-[#D3FF62] rounded-2xl flex items-center justify-center mb-6">
                    {IconComponent && <IconComponent className="w-7 h-7 text-[#004534]" />}
                  </div>
                  <h3 className="text-xl font-semibold text-[#004534] mb-4">{service.title}</h3>
                  <p className="text-[#807979] leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-[#004534] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#D3FF62] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D3FF62] rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 animate-on-scroll opacity-0">
              <span className="text-[#D3FF62] font-semibold uppercase tracking-wider">Why MLDC Agro</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                We Grow When Farmers Grow
              </h2>
              <p className="text-white/80 leading-relaxed">
                {aboutContent.commitment.description}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#D3FF62] rounded-full flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-5 h-5 text-[#004534]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-white/60 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative animate-on-scroll opacity-0">
              <img
                src={images.farmerInField}
                alt="Farmers working in grain field"
                className="rounded-3xl w-full h-[450px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-[#EDEDFE]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-10 shadow-sm animate-on-scroll opacity-0">
              <div className="w-14 h-14 bg-[#D3FF62] rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-[#004534]" />
              </div>
              <h3 className="text-2xl font-bold text-[#004534] mb-4">{aboutContent.vision.title}</h3>
              <p className="text-[#807979] leading-relaxed">{aboutContent.vision.description}</p>
            </div>
            
            <div className="bg-white rounded-3xl p-10 shadow-sm animate-on-scroll opacity-0">
              <div className="w-14 h-14 bg-[#D3FF62] rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb className="w-7 h-7 text-[#004534]" />
              </div>
              <h3 className="text-2xl font-bold text-[#004534] mb-4">{aboutContent.mission.title}</h3>
              <p className="text-[#807979] leading-relaxed">{aboutContent.mission.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blogs Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div className="animate-on-scroll opacity-0">
              <span className="text-[#0C6951] font-semibold uppercase tracking-wider">Knowledge Center</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mt-2">
                Latest Insights
              </h2>
            </div>
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-[#004534] font-semibold hover:gap-4 transition-all duration-200"
            >
              View All Blogs <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.slice(0, 3).map((blog, index) => (
              <article
                key={blog.id}
                className="bg-[#FAFAFF] rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-on-scroll opacity-0"
              >
                <div className="h-48 bg-gradient-to-br from-[#CACAFC] to-[#EDEDFE] flex items-center justify-center overflow-hidden">
                  {blog.image ? (
                    <img 
                      src={blog.image} 
                      alt={blog.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <Wheat className="w-16 h-16 text-[#004534]/30" />
                  )}
                </div>
                <div className="p-6">
                  <span className="text-sm text-[#807979]">{blog.date}</span>
                  <h3 className="text-lg font-semibold text-[#004534] mt-2 mb-3 line-clamp-2">
                    {blog.title}
                  </h3>
                  <p className="text-[#807979] text-sm line-clamp-2 mb-4">{blog.excerpt}</p>
                  <Link
                    to={`/blogs/${blog.slug}`}
                    className="inline-flex items-center gap-2 text-[#004534] font-medium text-sm hover:gap-3 transition-all duration-200"
                  >
                    Read More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#FAFFEE]">
        <div className="container mx-auto px-4">
          <div className="bg-[#004534] rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D3FF62] rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D3FF62] rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Partner With Us?
              </h2>
              <p className="text-white/80 mb-8">
                Whether you're a farmer looking for better markets or a business seeking quality agricultural produce, we're here to help.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#D3FF62] text-[#004534] px-8 py-4 rounded-full font-semibold hover:bg-[#c4f050] transition-all duration-200 hover:-translate-y-0.5"
                >
                  Get In Touch
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-[#004534] transition-all duration-200"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
